import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router';
import { ApiError } from '@/lib/api/errors';
import { t } from '@/lib/i18n';
import { AuthProvider } from '@/lib/auth/AuthContext';
import { AppShell } from '@/components/layout/AppShell';
import { RequireAnonymous, RequireAuth } from '@/routes/RequireAuth';
import { patterns, routes } from '@/routes/paths';
import { Login } from '@/routes/auth/Login';
import { Register } from '@/routes/auth/Register';
import { StatsSummary } from '@/routes/home/StatsSummary';
import { Timeline } from '@/routes/timeline/Timeline';
import { FriendsList } from '@/routes/friends/FriendsList';
import { AddFriend } from '@/routes/friends/AddFriend';
import { RoundsList } from '@/routes/rounds/RoundsList';
import { RoundDetail } from '@/routes/rounds/RoundDetail';
import { Profile } from '@/routes/profile/Profile';
import { RecordRound } from '@/routes/record/RecordRound';
import { PlayerProfile } from '@/routes/players/PlayerProfile';
import { PlayerRounds } from '@/routes/players/PlayerRounds';
import { JoinFlight } from '@/routes/record/JoinFlight';
import { HoleViewer } from '@/routes/hole/HoleViewer';
import { CoursesList } from '@/routes/courses/CoursesList';
import { CourseDetail } from '@/routes/courses/CourseDetail';
import { Stats } from '@/routes/stats/Stats';
import { ToastProvider } from '@/components/ui/Toast';
import { NativeNavigationBridge } from '@/lib/bridge/NativeNavigationBridge';
import { Showcase } from '@/routes/showcase/Showcase';
import { Placeholder } from '@/routes/Placeholder';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Course data barely changes; round data changes only when the user acts.
      staleTime: 60_000,
      // The client already replays once after a token refresh. Retrying a 4xx on top of
      // that just burns requests against a rate limit the user cannot see.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            {/* Dentro da casca nativa, liga o back de hardware ao histórico. No-op no navegador. */}
            <NativeNavigationBridge />
            <Routes>
              {/* Design system reference. Not linked from the app; useful while building. */}
              <Route path="/showcase" element={<Showcase />} />

              <Route element={<RequireAnonymous />}>
                <Route path={routes.login} element={<Login />} />
                <Route path={routes.register} element={<Register />} />
              </Route>

              <Route element={<RequireAuth />}>
                <Route element={<AppShell />}>
                  <Route index element={<Timeline />} />
                  <Route path={routes.friends} element={<FriendsList />} />
                  <Route path={routes.addFriend} element={<AddFriend />} />
                  <Route path={patterns.player} element={<PlayerProfile />} />
                  <Route path={patterns.playerRounds} element={<PlayerRounds />} />
                  <Route path={routes.rounds} element={<RoundsList />} />
                  <Route path={patterns.round} element={<RoundDetail />} />
                  <Route path={routes.record} element={<RecordRound />} />
                  <Route path={patterns.joinFlight} element={<JoinFlight />} />
                  <Route path={routes.courses} element={<CoursesList />} />
                  <Route path={patterns.course} element={<CourseDetail />} />
                  {/* O resumo pessoal (1c) e os gráficos (1l/1o) ficam em dois endereços:
                      a aba abre o resumo, e "Detalhados ▸" leva aos gráficos. */}
                  <Route path={routes.stats} element={<StatsSummary />} />
                  <Route path={routes.statsDetailed} element={<Stats />} />
                  <Route path={routes.profile} element={<Profile />} />
                </Route>

                {/* The hole viewer is full-bleed and dark — it deliberately sits outside
                  the shell, with no nav bar of its own (canvas 1f/1g). */}
                <Route path={patterns.hole} element={<HoleViewer />} />
              </Route>

              <Route path="*" element={<Placeholder title={t('common.notFound.title')} />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
